import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Paper,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessIcon from "@mui/icons-material/Business";
import TranslateIcon from "@mui/icons-material/Translate";
import TuneIcon from "@mui/icons-material/Tune";
import RestoreIcon from "@mui/icons-material/Restore";
import { toast } from "react-toastify";
import api from "../../../../conf/api";
import { getPlatformFeedType } from "../../Enums/platform_constants";

const DEFAULT_PROMPT =
  "You are a creative video metadata writer.\n\n" +
  "Rephrase the given content using noticeably different wording while preserving the exact same meaning, context, and intent.\n" +
  "The result should feel like it was written by a different person, not a light edit.\n\n" +
  "Requirements:\n" +
  "- Keep the tone, style, and emotional intent the same.\n" +
  "- Do not reuse the original phrasing or sentence structure.\n" +
  "- Maintain all brand names, product names, proper nouns, and technical terms exactly as provided.\n" +
  "- Ensure the output sounds natural, fluent, and engaging.\n" +
  "- Do not add new information or remove any meaning.\n" +
  "- Do not use any name if available in the description.\n\n" +
  "Output rules:\n" +
  "- Return only valid JSON.\n" +
  "- Do not include markdown, comments, or explanations.\n" +
  "- Do not include any extra text outside the JSON.";

// Native (Video Library) videos arrive with only a title. With no description
// there is nothing to preserve, so every platform is AI-generated (description
// and tags from the title) rather than left on the empty "original".
const needsGeneration = (video) => !(video?.description || "").trim();

const focusSx = {
  "&.Mui-focusVisible, &:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
};

const LANGUAGES = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese",
  "Dutch", "Russian", "Chinese", "Japanese", "Korean", "Arabic",
  "Hindi", "Turkish", "Polish", "Swedish", "Norwegian", "Danish",
  "Finnish", "Greek", "Czech", "Romanian", "Hungarian", "Thai",
  "Vietnamese", "Indonesian", "Malay", "Filipino", "Hebrew", "Ukrainian",
];

function KeywordsField({ value, onChange }) {
  const toRaw = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
  const [raw, setRaw] = React.useState(() => toRaw(value));

  React.useEffect(() => {
    setRaw(toRaw(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const commit = () => {
    const kw = raw.split(",").map((k) => k.trim()).filter(Boolean);
    onChange(kw);
  };

  return (
    <TextField
      fullWidth
      label="Keywords (comma-separated)"
      size="small"
      helperText="Separate keywords with commas"
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={commit}
    />
  );
}

export default function Step4RephrasedContent({
  selectedBrands,
  brandVideos,
  brandPlatforms,
  brandRephrasedContent,
  onRephrasedContentChange,
}) {
  const [brandPlatformPrompts, setBrandPlatformPrompts] = useState({});
  const [promptsLoaded, setPromptsLoaded] = useState({}); // { brandName: bool }

  const [loadingState, setLoadingState] = useState(() => {
    const init = {};
    (selectedBrands || []).forEach((brand) => {
      const platforms = (brandPlatforms || {})[brand.name] || [];
      const socialPlatforms = platforms.filter((p) => getPlatformFeedType(p) !== "PARTNER");
      const videos = (brandVideos || {})[brand.name] || [];
      videos.forEach((video) => {
        const generate = needsGeneration(video);
        const autoRephrasePlatforms = generate ? platforms : video.is_compilation ? [] : socialPlatforms;
        const alreadyDone =
          autoRephrasePlatforms.length > 0 &&
          autoRephrasePlatforms.every(
            (p) => brandRephrasedContent?.[brand.name]?.[video.unique_key]?.[p]?.title
          );
        if (!alreadyDone && autoRephrasePlatforms.length > 0) {
          if (!init[brand.name]) init[brand.name] = {};
          init[brand.name][video.unique_key] = {};
          autoRephrasePlatforms.forEach((p) => {
            init[brand.name][video.unique_key][p] = true;
          });
        }
      });
    });
    return init;
  });

  const [rephrasedPartnerPlatforms, setRephrasedPartnerPlatforms] = useState({});

  const [videoLanguages, setVideoLanguages] = useState({});

  const originalContent = (video) => ({
    title: video.title || video.video_title || "",
    description: video.description || "",
    keywords: getVideoOriginalKeywords(video),
  });

  useEffect(() => {
    selectedBrands.forEach(async (brand) => {
      if (promptsLoaded[brand.name]) return;
      try {
        const res = await api.get(
          `/mrss/brand-platform-prompts/?brand=${encodeURIComponent(brand.name)}`
        );
        const data = res.data?.results ?? res.data ?? [];
        const map = {};
        data.forEach((item) => {
          if (item.rephrase_prompt) map[item.platform] = item.rephrase_prompt;
        });
        setBrandPlatformPrompts((prev) => ({ ...prev, [brand.name]: map }));
      } catch (_) {
      } finally {
        setPromptsLoaded((prev) => ({ ...prev, [brand.name]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands]);
  const getVideoOriginalKeywords = (video) => {
    if (Array.isArray(video.keywords) && video.keywords.length > 0) return video.keywords;
    if (typeof video.tags === "string" && video.tags.trim().length > 0) {
      return video.tags.split(",").map(t => t.trim()).filter(Boolean);
    }
    if (Array.isArray(video.tags)) return video.tags;
    return [];
  };

  useEffect(() => {
    selectedBrands.forEach((brand) => {
      if (!promptsLoaded[brand.name]) return; // wait until prompts are fetched
      const videos = brandVideos[brand.name] || [];
      const platforms = brandPlatforms[brand.name] || [];
      const socialPlatforms = platforms.filter((p) => getPlatformFeedType(p) !== "PARTNER");
      const partnerPlatforms = platforms.filter((p) => getPlatformFeedType(p) === "PARTNER");

      videos.forEach((video) => {
        const generate = needsGeneration(video);
        const originalPlatforms = generate ? [] : video.is_compilation ? platforms : partnerPlatforms;
        const autoRephrasePlatforms = generate ? platforms : video.is_compilation ? [] : socialPlatforms;

        originalPlatforms.forEach((p) => {
          if (!brandRephrasedContent?.[brand.name]?.[video.unique_key]?.[p]?.title) {
            onRephrasedContentChange(brand.name, video.unique_key, [p], originalContent(video));
          }
        });

        if (autoRephrasePlatforms.length > 0) {
          const alreadyDone = autoRephrasePlatforms.every(
            (p) => brandRephrasedContent?.[brand.name]?.[video.unique_key]?.[p]?.title
          );
          if (!alreadyDone) {
            rephraseForVideo(brand.name, video, autoRephrasePlatforms, "English");
          }
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptsLoaded]);

  const setLoading = (brandName, videoKey, platforms, value) => {
    setLoadingState((prev) => {
      const next = { ...prev };
      if (!next[brandName]) next[brandName] = {};
      if (!next[brandName][videoKey]) next[brandName][videoKey] = {};
      platforms.forEach((p) => {
        next[brandName][videoKey][p] = value;
      });
      return next;
    });
  };

  /** Resolves the active prompt for a brand+platform pair. */
  const getPrompt = (brandName, platform) =>
    brandPlatformPrompts[brandName]?.[platform] || DEFAULT_PROMPT;

  /**
   * Rephrases a single platform. Returns true on success, false on failure.
   * Does not toast — callers handle user feedback.
   */
  const rephraseForPlatform = async (brandName, video, platform, language) => {
    const prompt = getPrompt(brandName, platform);
    setLoading(brandName, video.unique_key, [platform], true);
    try {
      const response = await api.post(
        "/mrss/rephrase/",
        {
          title: video.title || video.video_title || "",
          description: video.description || "",
          keywords: getVideoOriginalKeywords(video),
          target_language: language,
          platform: platform,
          custom_prompt: prompt !== DEFAULT_PROMPT ? prompt : undefined,
        },
        { timeout: 6 * 60 * 1000 }
      );
      if (response.data.success) {
        const { title, description, keywords } = response.data;
        onRephrasedContentChange(brandName, video.unique_key, [platform], {
          title,
          description,
          keywords,
        });
        return true;
      }
      throw new Error(response.data.error || "Rephrasing failed");
    } catch {
      onRephrasedContentChange(brandName, video.unique_key, [platform], originalContent(video));
      return false;
    } finally {
      setLoading(brandName, video.unique_key, [platform], false);
    }
  };

  /** Restores the original title, description, and tags for a platform */
  const handleRestoreOriginal = (brandName, video, platform) => {
    onRephrasedContentChange(brandName, video.unique_key, [platform], originalContent(video));
    toast.info(`Restored original content for ${platform}`);
  };

  /** Rephrases all platforms for a video in parallel (each uses its brand+platform prompt). */
  const rephraseForVideo = async (brandName, video, platforms, language) => {
    if (!platforms.length) return;
    const results = await Promise.all(
      platforms.map((p) => rephraseForPlatform(brandName, video, p, language))
    );
    const succeeded = results.filter(Boolean).length;
    const failed = platforms.length - succeeded;
    if (failed === 0) {
      toast.success(`Rephrased "${video.title || video.unique_key}" for all platforms`);
    } else {
      toast.warn(
        `Rephrased ${succeeded}/${platforms.length} platforms for "${
          video.title || video.unique_key
        }"`
      );
    }
  };

  /** Called by the per-platform refresh icon button. */
  const handleRefreshPlatform = async (brandName, video, platform, language) => {
    const ok = await rephraseForPlatform(brandName, video, platform, language);
    if (ok) {
      if (getPlatformFeedType(platform) === "PARTNER" || video.is_compilation || needsGeneration(video)) {
        setRephrasedPartnerPlatforms((prev) => ({
          ...prev,
          [`${brandName}__${video.unique_key}__${platform}`]: true,
        }));
      }
      toast.success(`Re-rephrased ${platform}`);
    } else {
      toast.error(`Re-rephrase failed for ${platform}`);
    }
  };

  const handleFieldChange = (brandName, videoKey, platform, field, value) => {
    const current = brandRephrasedContent?.[brandName]?.[videoKey]?.[platform] || {};
    onRephrasedContentChange(brandName, videoKey, [platform], { ...current, [field]: value });
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 4 — Rephrase Content
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        AI rephrases title, description and keywords per platform using prompts configured in{" "}
        <strong>Brand Settings → Platform Rephrase Prompts</strong>.
      </Typography>
      <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
        To change AI prompts, go to <strong>Brand Management</strong>, edit the brand, and expand{" "}
        <strong>Platform Rephrase Prompts</strong>.
      </Alert>
      {selectedBrands.length === 0 && (
        <Alert severity="warning">No brands selected.</Alert>
      )}

      {selectedBrands.map((brand) => {
        const videos = brandVideos[brand.name] || [];
        const platforms = brandPlatforms[brand.name] || [];
        const isPromptLoaded = promptsLoaded[brand.name];
        const customCount = platforms.filter(
          (p) => !!brandPlatformPrompts[brand.name]?.[p]
        ).length;

        return (
          <Accordion
            key={brand.name}
            defaultExpanded
            disableGutters
            variant="outlined"
            sx={{ mb: 1, borderRadius: 2, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, ...focusSx }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                <BusinessIcon color="primary" fontSize="small" aria-hidden />
                <Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}>{brand.name}</Typography>
                <Chip
                  label={`${videos.length} video${videos.length !== 1 ? "s" : ""}`}
                  size="small"
                  color="primary"
                />
                {isPromptLoaded && customCount > 0 && (
                  <Chip
                    icon={<TuneIcon style={{ fontSize: 14 }} />}
                    label={`${customCount} custom prompt${customCount !== 1 ? "s" : ""}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
              {!isPromptLoaded && (
                <Box display="flex" alignItems="center" gap={1} mb={2} role="status">
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Loading prompt settings…
                  </Typography>
                </Box>
              )}
              {videos.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No videos for this brand.
                </Typography>
              )}

              {videos.map((video) => {
                const vidKey = `${brand.name}__${video.unique_key}`;
                const lang = videoLanguages[vidKey] || "English";
                const anyLoading = platforms.some(
                  (p) => loadingState[brand.name]?.[video.unique_key]?.[p]
                );
                const filledCount = platforms.filter(
                  (p) => brandRephrasedContent?.[brand.name]?.[video.unique_key]?.[p]?.title
                ).length;

                return (
                  <Box key={video.unique_key} mb={3}>
                    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5, borderRadius: 2 }}>
                      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                            {video.title || video.video_title || video.unique_key}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                              {video.unique_key}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={filledCount === platforms.length ? "success" : "warning"}
                              label={`${filledCount}/${platforms.length} titles filled`}
                            />
                          </Stack>
                        </Box>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                          <FormControl size="small" sx={{ minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}>
                            <InputLabel id={`lang-${vidKey}`}>Language</InputLabel>
                            <Select
                              labelId={`lang-${vidKey}`}
                              value={lang}
                              label="Language"
                              sx={{ minHeight: 40 }}
                              startAdornment={<TranslateIcon fontSize="small" sx={{ mr: 0.5 }} aria-hidden />}
                              onChange={(e) =>
                                setVideoLanguages((prev) => ({ ...prev, [vidKey]: e.target.value }))
                              }
                            >
                              {LANGUAGES.map((l) => (
                                <MenuItem key={l} value={l}>
                                  {l}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            startIcon={anyLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                            onClick={() => rephraseForVideo(brand.name, video, platforms, lang)}
                            disabled={anyLoading}
                            sx={{ width: { xs: "100%", sm: "auto" }, minHeight: { xs: 44, sm: 36 }, ...focusSx }}
                          >
                            {anyLoading ? "Rephrasing…" : "Rephrase All Platforms"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>

                    <Grid container spacing={1.5}>
                      {platforms.map((platform) => {
                        const content =
                          brandRephrasedContent?.[brand.name]?.[video.unique_key]?.[
                            platform
                          ] || {};
                        const isLoading =
                          !!loadingState[brand.name]?.[video.unique_key]?.[platform];
                        const hasCustomPrompt =
                          !!brandPlatformPrompts[brand.name]?.[platform];
                        const isPartner = getPlatformFeedType(platform) === "PARTNER";
                        const usesOriginalByDefault = (isPartner || video.is_compilation) && !needsGeneration(video);
                        const partnerKey = `${brand.name}__${video.unique_key}__${platform}`;
                        const wasRephrased = !!rephrasedPartnerPlatforms[partnerKey];
                        const setField = (f, v) =>
                          handleFieldChange(brand.name, video.unique_key, platform, f, v);
                        const refresh = () => handleRefreshPlatform(brand.name, video, platform, lang);

                        return (
                          <Grid item xs={12} md={6} key={platform} sx={{ minWidth: 0 }}>
                            <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} mb={1.5}>
                                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip label={platform} size="small" color="primary" />
                                    <Chip
                                      label={content.title ? "Title filled" : "Title missing"}
                                      size="small"
                                      color={content.title ? "success" : "warning"}
                                      variant="outlined"
                                    />
                                    {hasCustomPrompt && (
                                      <Tooltip title="Using a custom prompt configured in Brand Settings">
                                        <Chip
                                          label="custom prompt"
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                        />
                                      </Tooltip>
                                    )}
                                    {usesOriginalByDefault && !isLoading && (
                                      wasRephrased ? (
                                        <Chip
                                          icon={<AutoAwesomeIcon style={{ fontSize: 14 }} />}
                                          label="AI Rephrased"
                                          size="small"
                                          color="secondary"
                                          variant="outlined"
                                        />
                                      ) : (
                                        <Chip
                                          label="Using Original"
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                        />
                                      )
                                    )}
                                  </Stack>
                                  <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Tooltip title="Use Original Content">
                                      <span>
                                        <IconButton
                                          aria-label={`Use original content for ${platform}`}
                                          onClick={() => handleRestoreOriginal(brand.name, video, platform)}
                                          disabled={isLoading}
                                          sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 }, ...focusSx }}
                                        >
                                          <RestoreIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    {usesOriginalByDefault && !isLoading && !wasRephrased && (
                                      <Tooltip title="Rephrase this platform with AI">
                                        <Button
                                          variant="outlined"
                                          color="secondary"
                                          startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                                          onClick={refresh}
                                          sx={{ textTransform: "none", minHeight: { xs: 44, sm: 36 }, ...focusSx }}
                                        >
                                          AI Rephrase
                                        </Button>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={usesOriginalByDefault && !wasRephrased ? "AI Rephrase this platform" : "Re-rephrase this platform"}>
                                      <span>
                                        <IconButton
                                          aria-label={`${usesOriginalByDefault && !wasRephrased ? "AI rephrase" : "Re-rephrase"} ${platform}`}
                                          onClick={refresh}
                                          disabled={isLoading}
                                          sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 }, ...focusSx }}
                                        >
                                          {isLoading ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <RefreshIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                </Stack>

                                {isLoading ? (
                                  <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    py={3}
                                    role="status"
                                  >
                                    <CircularProgress size={28} />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      ml={1}
                                    >
                                      Rephrasing…
                                    </Typography>
                                  </Box>
                                ) : (
                                  <>
                                    {!content.title && (
                                      <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                                        No content yet — click "Rephrase All Platforms" above.
                                      </Alert>
                                    )}
                                    <TextField
                                      fullWidth
                                      label="Title"
                                      size="small"
                                      required
                                      sx={{ mb: 1.5 }}
                                      value={content.title || ""}
                                      error={!content.title}
                                      helperText={!content.title ? "Title is required to publish" : " "}
                                      onChange={(e) =>
                                        setField("title", e.target.value)
                                      }
                                    />
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      size="small"
                                      multiline
                                      minRows={3}
                                      maxRows={10}
                                      sx={{ mb: 1.5 }}
                                      value={content.description || ""}
                                      onChange={(e) =>
                                        setField("description", e.target.value)
                                      }
                                    />
                                    <KeywordsField
                                      value={content.keywords}
                                      onChange={(kw) =>
                                        setField("keywords", kw)
                                      }
                                    />
                                  </>
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
